/**
 * Collection nav carousel
 *
 * A lightweight horizontal carousel of collection cards:
 *  - swipes/scrolls natively (with scroll-snap) on touch devices
 *  - is draggable with the mouse, and can be advanced with prev/next arrows, on desktop
 *
 * Loaded only on pages that render the "Collection nav carousel" section (see
 * sections/collection-nav-carousel.liquid), so it stays out of the theme's compiled
 * theme.js/custom.js bundles.
 */
(function () {
  if (window.customElements.get('collection-nav-carousel')) {
    return;
  }

  var DRAG_THRESHOLD = 5; // px of movement before a pointer interaction counts as a drag, not a click

  class CollectionNavCarousel extends HTMLElement {
    connectedCallback() {
      this.scroller = this.querySelector('.collection-nav-carousel__scroller');
      this.items = Array.prototype.slice.call(this.querySelectorAll('.collection-nav-carousel__item'));
      this.prevButton = this.querySelector('.collection-nav-carousel__arrow--prev');
      this.nextButton = this.querySelector('.collection-nav-carousel__arrow--next');

      if (!this.scroller || this.items.length === 0) {
        return;
      }

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onClickCapture = this._onClickCapture.bind(this);
      this._onDragStart = this._onDragStart.bind(this);
      this._onDraggabilityChange = this._onDraggabilityChange.bind(this);

      this.addEventListener('prev-next:prev', this._onPrev);
      this.addEventListener('prev-next:next', this._onNext);

      this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
      // Belt-and-suspenders: cards are anchors, which browsers make natively draggable by
      // default. `draggable="false"` on the item covers most cases, but Firefox in particular
      // can still start a native drag from a link's text, so also swallow "dragstart" outright.
      // A native drag stealing the gesture is what breaks our own pointer-based drag mid-swipe.
      this.scroller.addEventListener('dragstart', this._onDragStart);
      window.addEventListener('resize', this._onResize);

      this._setupDraggability();
      this._updateArrows();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this.scroller.removeEventListener('dragstart', this._onDragStart);
      this._removeDragListeners();
    }

    _onDragStart(event) {
      event.preventDefault();
    }

    _direction() {
      return window.themeVariables &&
        window.themeVariables.settings &&
        window.themeVariables.settings.direction === 'rtl'
        ? -1
        : 1;
    }

    _setupDraggability() {
      this._dragMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

      if (this._dragMediaQuery.addEventListener) {
        this._dragMediaQuery.addEventListener('change', this._onDraggabilityChange);
      } else if (this._dragMediaQuery.addListener) {
        // Safari < 14
        this._dragMediaQuery.addListener(this._onDraggabilityChange);
      }

      if (this._dragMediaQuery.matches) {
        this._attachDragListeners();
      }
    }

    _onDraggabilityChange(event) {
      if (event.matches) {
        this._attachDragListeners();
      } else {
        this._removeDragListeners();
      }
    }

    _attachDragListeners() {
      if (this._dragListenersAttached) {
        return;
      }
      this._dragListenersAttached = true;
      this.scroller.addEventListener('pointerdown', this._onPointerDown);
      this.scroller.addEventListener('click', this._onClickCapture, true);
    }

    _removeDragListeners() {
      this._dragListenersAttached = false;
      this.scroller.removeEventListener('pointerdown', this._onPointerDown);
      this.scroller.removeEventListener('click', this._onClickCapture, true);
    }

    _onPointerDown(event) {
      // Only the primary mouse button should start a drag.
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      // Tracking starts immediately, but this is provisional: until movement clears
      // DRAG_THRESHOLD (in _onPointerMove) we don't know yet whether this is a drag or a
      // plain click, so nothing visually or behaviorally "drag-like" happens here. A
      // stationary press-and-release must be indistinguishable from a click.
      this.dragging = true;
      this.dragMoved = false;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.scroller.scrollLeft;

      // Note: pointer capture is NOT acquired here. Capturing a pointer retargets every
      // subsequent event for it -- including the derived mouseup/click -- to the capturing
      // element. Doing that unconditionally on pointerdown silently broke every card link,
      // since the click was redirected to the scroller before it could ever reach the <a>.
      this.scroller.addEventListener('pointermove', this._onPointerMove);
      this.scroller.addEventListener('pointerup', this._onPointerUp);
      this.scroller.addEventListener('pointercancel', this._onPointerUp);
    }

    _onPointerMove(event) {
      if (!this.dragging) {
        return;
      }

      var deltaX = event.clientX - this.dragStartX;

      if (!this.dragMoved && Math.abs(deltaX) > DRAG_THRESHOLD) {
        // Threshold cleared for the first time this gesture: commit to "dragging" now, and
        // only now (cursor, scroll-snap suspension, click suppression on release, and taking
        // over the pointer so the drag keeps tracking even if the cursor leaves the scroller).
        this.dragMoved = true;
        this.scroller.classList.add('is-dragging');
        this.scroller.setPointerCapture(event.pointerId);
      }

      if (this.dragMoved) {
        this.scroller.scrollLeft = this.dragStartScrollLeft - deltaX;
      }
    }

    _onPointerUp(event) {
      this.dragging = false;
      this.scroller.classList.remove('is-dragging');
      this.scroller.removeEventListener('pointermove', this._onPointerMove);
      this.scroller.removeEventListener('pointerup', this._onPointerUp);
      this.scroller.removeEventListener('pointercancel', this._onPointerUp);

      if (this.scroller.hasPointerCapture && this.scroller.hasPointerCapture(event.pointerId)) {
        this.scroller.releasePointerCapture(event.pointerId);
      }
    }

    _onClickCapture(event) {
      // Suppress the navigation click that follows a drag (mirrors the drag-then-click
      // problem any pointer-based scroller has to solve).
      if (this.dragMoved) {
        event.preventDefault();
        event.stopPropagation();
        this.dragMoved = false;
      }
    }

    _step() {
      var firstItem = this.items[0];
      var trackStyle = window.getComputedStyle(this.scroller.firstElementChild);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      var itemWidth = firstItem.getBoundingClientRect().width;
      var visibleCount = Math.max(1, Math.floor((this.scroller.clientWidth + gap) / (itemWidth + gap)));

      return visibleCount * (itemWidth + gap);
    }

    _onPrev() {
      this.scroller.scrollBy({ left: -this._step() * this._direction(), behavior: 'smooth' });
    }

    _onNext() {
      this.scroller.scrollBy({ left: this._step() * this._direction(), behavior: 'smooth' });
    }

    _onScroll() {
      if (this._scrollTicking) {
        return;
      }
      this._scrollTicking = true;
      window.requestAnimationFrame(() => {
        this._updateArrows();
        this._scrollTicking = false;
      });
    }

    _onResize() {
      this._updateArrows();
    }

    _updateArrows() {
      if (!this.prevButton || !this.nextButton) {
        return;
      }

      var maxScrollLeft = this.scroller.scrollWidth - this.scroller.clientWidth;
      var scrollLeft = Math.abs(this.scroller.scrollLeft);
      var tolerance = 1;

      this.prevButton.disabled = scrollLeft <= tolerance;
      this.nextButton.disabled = maxScrollLeft <= tolerance || scrollLeft >= maxScrollLeft - tolerance;
    }
  }

  window.customElements.define('collection-nav-carousel', CollectionNavCarousel);
})();
